import Rails from '@rails/ujs';
import * as L from 'leaflet';

const defaultMapCenter = ["38.716946", "-9.142685"];
const defaultZoomLevel = 16;
const defaultIconSize = [31, 31];
const defaultDotIconSize = [defaultIconSize[0] / 2, defaultIconSize[1] / 2]
const defaultIconAnchor = [15.5, 31];
// use the height of the icon size to define the popup anchor position
// defaultIconSize[1]
const defaultPopupAnchor = [0, -defaultIconSize[1]];
const unselectedIcon = "https://storage.googleapis.com/poetic-primer-235017.appspot.com/public/assets/icons/black/place-map-filled-56e831e13de0a8f0afe3c0f23a41bf27e36b9b4281eb14e48eff62b4a0e20041.svg";
const selectedIcon = "https://storage.googleapis.com/poetic-primer-235017.appspot.com/public/assets/icons/black/place-selected-0cd6f9fccdd872821b249cc699a18fd6e7afaf574c78360efcb69c433c64287e.svg";
const dotIcon = "https://storage.googleapis.com/poetic-primer-235017.appspot.com/public/assets/icons/black/circle-520387d63397ae8d49772649e77f02a5202f26e7a02a80bb3ec227947eba392d.svg"
let map;

const mapPin = L.icon({
  iconUrl: unselectedIcon,
  iconSize: defaultIconSize,
  iconAnchor: defaultIconAnchor,
  popupAnchor: defaultPopupAnchor,
  className: 'map-icons'
});

const mapDot = L.icon({
  iconUrl: dotIcon,
  iconSize: defaultDotIconSize
});

export const loadMaps = () => {
  const maps = document.querySelectorAll("[data-map]");

  maps.forEach((mapElement) => {
    // map instance
    map = L.map(mapElement);
    // get the map type
    // we need this because we will need to defined certain
    // actions depending on the type
    const mapType = mapElement.getAttribute('data-map')

    // define the maptiles and attribution to use
    L.tileLayer("https://api.maptiler.com/maps/basic/{z}/{x}/{y}.png?key=DQlKVnypahgTsV5y9fQ0", {
      attribution: '<a href="https://www.maptiler.com/copyright/" target="_blank">© MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>',
      maxZoom: 18
    }).addTo(map);

    switch (mapType) {
      case 'listing':
        listingMap(map, mapElement);
        break
      case 'location':
        locationMap(map, mapElement);
        break
      case 'searchable':
        searchableMap(map, mapElement);
        break
    }
  })
}

const listingMap = (map, element) => {
  const pin = getPin(element)
  L.marker(pin, { icon: mapPin }).addTo(map);
  map.setView(pin, defaultZoomLevel)
}

const locationMap = (map, element) => {
  const bb = getBoundingBox(element);

  if (isMarker(bb) && !bb.geoShape) {
    const marker = returnMarker(bb);
    L.marker(marker, { icon: mapPin }).addTo(map);
    map.setView(marker, defaultZoomLevel)
  } else if (isCompleteBoundingBox(bb)) {
    const bounds = returnArrayBounds(bb)
    L.rectangle(bounds, { color: "#ff7800", weight: 1 }).addTo(map);
    map.fitBounds(bounds);
  } else {
    // Its a shape location
    const shapeStyle = {
      "color": "#ff7800",
      "weight": 2,
      "opacity": 0.65
    };
    const feature = L.geoJSON(bb.geoShape, shapeStyle).addTo(map);
    map.fitBounds(feature.getBounds());
  }
}

const searchableMap = (map, element) => {
  const bb = getBoundingBox(element);
  if (isCompleteBoundingBox(bb)) {
    const bounds = returnArrayBounds(bb)
    map.fitBounds(bounds);
  } else {
    map.setView(defaultMapCenter, defaultZoomLevel)
  }

  const searchForm = document.querySelector('#search');
  if (searchForm) {
    const searchBtn = document.querySelector('#map-research')
    const searchFormElements = searchForm.elements

    renderMarkers()
    renderDotMarkers()

    searchBtn.addEventListener('click', () => {
      const ne = [map.getBounds()._northEast.lat, map.getBounds()._northEast.lng]
      const sw = [map.getBounds()._southWest.lat, map.getBounds()._southWest.lng]
      searchFormElements.search_ne.value = ne
      searchFormElements.search_sw.value = sw
      searchFormElements.search_map.value = true
      Rails.fire(searchForm, 'submit')
    })

    map.addEventListener('movestart', (evt) => {
      searchBtn.classList.remove('hide');
      searchFormElements.search_location.value = ''
      searchFormElements.search_location_id.value = ''
    })

    map.addEventListener('popupopen', (ev) => {
      ev.popup._source._icon.src = selectedIcon;
    })

    map.addEventListener('popupclose', (ev) => {
      ev.popup._source._icon.src = unselectedIcon;
      resetSelected();
    });
  }
}

// Get the bounding box if defined
const getBoundingBox = (element) => {
  return {
    boundingBoxSouthwestLatitude: element.getAttribute('data-bb-sw-lat'),
    boundingBoxSouthwestLongitude: element.getAttribute('data-bb-sw-lng'),
    boundingBoxNortheastLatitude: element.getAttribute('data-bb-ne-lat'),
    boundingBoxNortheastLongitude: element.getAttribute('data-bb-ne-lng'),
    geoShape: JSON.parse(element.getAttribute('data-geo-shape'))
  }
}

// Get pin values from data attributes
const getPin = (element) => {
  return [element.getAttribute('data-pin-latitude'), element.getAttribute('data-pin-longitude')]
}

// Return an array with the bounding box values
const returnArrayBounds = (bb) => {
  return [
    [bb.boundingBoxSouthwestLatitude, bb.boundingBoxSouthwestLongitude],
    [bb.boundingBoxNortheastLatitude, bb.boundingBoxNortheastLongitude]
  ]
}

// Given a bounding box returns a marker
// Note that this will only return the SW latitute and longitude
// because if a bounding box represents a marker
// that means that both SW and NE latitude and longitude are the sane
// See isMarker
const returnMarker = (bb) => {
  return [bb.boundingBoxSouthwestLatitude, bb.boundingBoxSouthwestLongitude]
}

// Returns true if a bounding box has all corners defined
const isCompleteBoundingBox = (bb) => {
  return !!(bb.boundingBoxSouthwestLatitude && bb.boundingBoxSouthwestLongitude && bb.boundingBoxNortheastLatitude && bb.boundingBoxNortheastLongitude)
}

// If both latitudes and longitudes are the same
// then this is a marker
const isMarker = (bb) => {
  return (bb.boundingBoxSouthwestLatitude == bb.boundingBoxNortheastLatitude) && (bb.boundingBoxSouthwestLongitude == bb.boundingBoxNortheastLongitude)
}

// Reset styles for any selected listing
const resetSelected = () => {
  const currentSelected = document.querySelectorAll('.listing.selected');
  currentSelected.forEach((selected) => {
    selected.classList.remove('selected')
  })
}

// Get listing data from data elements or from HTML content
const getListingData = (element) => {
  const link = element.querySelector('a');
  const listingImage = element.querySelector('[data-marker-image] img');
  const listingName = element.querySelector('[data-marker-listing-name]');
  const listingDescription = element.querySelector('[data-marker-listing-description]')
  const latitude = element.getAttribute('data-latitude');
  const longitude = element.getAttribute('data-longitude');

  return {
    link: link ? link.href : '',
    image: listingImage ? listingImage.src : '',
    name: listingName ? listingName.textContent : '',
    description: listingDescription ? listingDescription.textContent : '',
    latitude: latitude,
    longitude: longitude
  }
}

// Render the markers on the map and attach popup events
const renderMarkers = () => {
  const listings = document.querySelectorAll('[data-map-marker]')

  listings.forEach((element) => {
    const listingData = getListingData(element);
    const customPopup = `
      <a href='${listingData.link}' target="_blank">
        <div class='map-card-wrapper flex-container'>
            <div class='image-card-map'>
              <img src="${listingData.image}" />
            </div>
            <div class='section-card-map flex-contaner'>
              <div class='mini-search-breadcrumbs-map'>
              </div>
              <h5 class='listing-name'>
                ${listingData.name}
              </h5>
              <div class='flex-container'>
                <div class= 'listing-comodities listing-comodities-info description-text'>
                  <p>
                    ${listingData.description}
                  </p>
                </div>
              </div>
              <div class='rating-map flex-container'>
              </div>
            </div>
        </div>
        </a>`
    if (listingData.latitude && listingData.longitude) {
      const marker = L.marker([listingData.latitude, listingData.longitude], { icon: mapPin }).addTo(map);
      marker.bindPopup(customPopup, {
        minWidth: 250,
        maxWidth: 300,
        closeButton: false
      })
      marker.addEventListener('click', (evt) => {
        map.panTo(evt.target.getLatLng());
        resetSelected()
        element.classList.add('selected')
      })
    }
  });
}

// Render dots on map that represent the listings
// From the next page
const renderDotMarkers = () => {
  const dotMarkers = document.querySelectorAll('[data-map-marker-next]')

  dotMarkers.forEach((element) => {
    const listingData = getListingData(element);
    if (listingData.latitude && listingData.longitude) {
      L.marker([listingData.latitude, listingData.longitude], { icon: mapDot }).addTo(map);
    }
  });
}

